// RFC 9457 problem → toast mapper. Lives in the shell, not in @companyos/ui (S1-15 step 6):
// the design system knows toasts, the app knows problems.
import { ApiError } from "@companyos/sdk";
import { AuthError } from "./client";

export type ProblemToast = {
  title: string;
  description?: string;
  type: "error" | "warning";
};

const MAX_FIELD_ERRORS = 3;

const BILLING_TITLES: Partial<Record<string, string>> = {
  ag_cap_reached: "This DE is at its tier's AG limit. Move it up a tier first.",
  billing_not_configured: "Payments are not set up yet, so nothing was charged or sent.",
  payment_provider_error: "Stripe could not do that just now. Nothing changed; try again.",
  subscription_suspended: "Billing is suspended until the overdue invoice is paid.",
};

/**
 * Returns null for errors the shell handles elsewhere (401 → sign-in redirect; 403
 * `mfa_enrollment_required` → the two-step enrolment screen).
 */
export function problemToToast(error: unknown): ProblemToast | null {
  if (error instanceof ApiError) {
    if (error.status === 401) return null;
    // HARDEN-mfa: the shell shows the two-step enrolment screen instead (session-expiry.ts).
    if (error.code === "mfa_enrollment_required") return null;
    const p = error.problem;
    if (!p) return { title: "Request failed", description: `HTTP ${error.status}`, type: "error" };
    // BILLING-PLANS: the money problems get a short, plain title; the API's detail stays below.
    const billing = BILLING_TITLES[p.code];
    if (billing)
      return p.detail
        ? { title: billing, description: p.detail, type: "warning" }
        : { title: billing, type: "warning" };
    let description = p.detail;
    if (!description && p.errors?.length) {
      const shown = p.errors.slice(0, MAX_FIELD_ERRORS).map((e) => `${e.path}: ${e.message}`);
      const more = p.errors.length - shown.length;
      description = shown.join("; ") + (more > 0 ? ` (+${more} more)` : "");
    }
    if (p.code === "feature_read_only" && !description) {
      description = p.read_only_until
        ? `Read-only until ${p.read_only_until}.`
        : "This add-on is read-only for this company.";
    }
    if (p.code === "feature_disabled" && !description) {
      description = `The ${p.feature} add-on is not enabled for this company.`;
    }
    const type = p.code === "feature_read_only" || p.status === 409 ? "warning" : "error";
    return description ? { title: p.title, description, type } : { title: p.title, type };
  }
  // Better Auth's routes (`/v1/auth/*`): the adapter already wrote a readable message.
  if (error instanceof AuthError) {
    return error.status === 401 ? null : { title: error.message, type: "error" };
  }
  if (error instanceof TypeError) {
    return {
      title: "Can't reach CompanyOS",
      description: "Check your connection and try again.",
      type: "error",
    };
  }
  const message = error instanceof Error ? error.message : String(error);
  return { title: "Something went wrong", description: message, type: "error" };
}

export type ToastSink = {
  add(options: { title: string; description?: string; type?: string }): unknown;
};

export function notifyProblem(error: unknown, sink: ToastSink): void {
  const toast = problemToToast(error);
  if (toast) sink.add(toast);
}
